import { useCallback, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Check, ChevronsUpDown, RotateCw } from 'lucide-react';
import { Badge, Command, Popover, PopoverContent, PopoverTrigger, Skeleton, Tooltip } from '@vnetwork/ui/primitives';
import {
  SHARED_BADGE,
  iamProjectStatus,
  sameId,
  sharedConsoleProject,
  sharedOrgName,
  switcherProjects,
  useIamProjects,
  useOrgCustomerId,
  useSharedProjects,
  useSharedWorkspaces,
  useWorkingProject,
} from '@/api/iam';
import { paths } from '@/lib/paths';
import { FolderIcon } from '../shell-icons';
import topbar from '../AppTopbar.module.css';
import {
  BACK_ROW_LABEL,
  SHARED_HEADING,
  allProjectsRow,
  failedRow,
  homeOrgNameOf,
  runsRevokedCheck,
  searchAllText,
  panelModel,
  pickPayload,
  openProjectId,
  showOpenProject,
  switcherLoading,
  rowSpoken,
  switchedLabel,
  triggerLabel,
  viewProjects,
  type ProjectView,
  type SwitcherVariant,
  type TriggerLabel,
} from './model';
import styles from './ProjectSwitcher.module.css';
import { useApplyProjectChoice, useRevokedChoice } from './useApplyProjectChoice';

function TriggerText({ label }: { label: TriggerLabel }) {
  return (
    <>
      <span className={styles.triggerName}>{label.name}</span>
      {label.hint && <span className={`${styles.muted} ${styles.fixed}`}>{label.hint}</span>}
      {label.shared && (
        <Badge tone="info" appearance="subtle" size="sm" className={styles.fixed}>
          {SHARED_BADGE}
        </Badge>
      )}
      {label.org && <span className={styles.triggerOrg}>{`· ${label.org}`}</span>}
    </>
  );
}

function ProjectRow({
  view,
  current,
  qualify,
  disabled,
  onPick,
}: {
  view: ProjectView;
  current: boolean;
  qualify: boolean;
  disabled: boolean;
  onPick: () => void;
}) {
  const { project, foreign, idHint } = view;
  return (
    <Command.Item
      value={`${project.organizationId}:${project.id}`}
      className={styles.item}
      aria-label={rowSpoken(view, current)}
      disabled={disabled}
      onSelect={onPick}
    >
      <span className={styles.itemName}>
        {project.name}
        {idHint && <span className={styles.muted}>{` ${idHint}`}</span>}
        {qualify && view.orgLabel && <span className={styles.muted}>{` · ${view.orgLabel}`}</span>}
      </span>
      <span className={styles.itemEnd}>
        {foreign && (
          <Badge tone="info" appearance="subtle" size="sm">
            {SHARED_BADGE}
          </Badge>
        )}
        {current && <Check size={14} aria-hidden />}
      </span>
    </Command.Item>
  );
}

/**
 * The project the person is working in. Choosing one stores it (`useWorkingProject`, with its organization): the label
 * shows it on every page and the create forms start from it. "All projects" clears it. The list is grouped by
 * organization; projects of another organization carry the Shared badge. A `Command` in a `Popover`, not a
 * `DropdownMenu`: the Radix menu has `role="group"` (`DropdownMenuGroup`) but no search input, and its typeahead and
 * focus handling take the keys a search field needs. `strip` is the same switcher drawn as the one-line context strip
 * below 768px, where the top bar hides it.
 */
export function ProjectSwitcher({ variant = 'topbar' }: { variant?: SwitcherVariant }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: projects = [], isLoading } = useIamProjects(undefined, false, { enabled: true });
  const { project: working, stored, settled, choose } = useWorkingProject();
  const { apply, back, workspacesSettled, foreignOrgName, unresolvedConsole } = useApplyProjectChoice();
  const { data: sharedProjects = [], isLoading: sharedLoading, isError: sharedFailed, refetch } = useSharedProjects();
  const { data: workspaces = [] } = useSharedWorkspaces();
  const home = useOrgCustomerId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [announcement, setAnnouncement] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  const views = useMemo(
    () =>
      viewProjects(
        switcherProjects(projects.filter((p) => iamProjectStatus(p) === 'active'), sharedProjects, home),
        homeOrgNameOf(sharedProjects, home),
      ),
    [projects, sharedProjects, home],
  );
  // A stored project that was archived, revoked or filed under another organization is not "working" any more.
  // A project of another organization is only known once the shared list has loaded: do not clear it before (or if it failed).
  const workingId = working?.projectId ?? '';
  const clearChoice = useCallback(() => choose(null), [choose]);
  useRevokedChoice(runsRevokedCheck(variant) && settled && !!stored && !working, stored, clearChoice);

  // On a project's own page the label follows that page when nothing is chosen.
  const routeId = location.pathname.match(/\/projects\/([^/]+)/)?.[1];
  const consoleId = location.pathname.match(/\/services\/compute\/([^/]+)/)?.[1];
  const workspace = workspaces.find((w) => w.metadata_id === consoleId);
  const sharedId = sharedConsoleProject(consoleId, workspaces, sharedProjects, workingId);
  const workspaceOrg =
    (workspace && workspace.organization_id !== home ? sharedOrgName(consoleId, workspaces, sharedProjects) : undefined) ?? foreignOrgName;
  const workingForeign = !!working && !!home && !sameId(working.organizationId, home);
  const currentId = openProjectId({ sharedId, workingId, workingForeign, unresolvedConsole });
  const shown = views.find((v) => v.project.id === (currentId || routeId));
  const loading = switcherLoading({ isLoading, stored: !!stored, settled, sharedFailed });
  const label = triggerLabel({ view: shown, workspaceOrgName: workspaceOrg, loading });
  const title =
    workingId && !shown && sharedFailed ? 'Your chosen project could not be loaded. Reload to try again.' : label.spoken;

  const model = panelModel(views, { currentId, query, sharedPending: sharedLoading || sharedFailed });
  const allRow = allProjectsRow(workspaceOrg);
  const searching = query.trim() !== '';
  const homeKnown = !!home;

  const pick = (view?: ProjectView) => {
    const payload = view ? pickPayload(view, homeKnown, workspacesSettled) : null;
    if (view && !payload) return;
    apply(payload, view ? switchedLabel(view) : allRow.label);
    setAnnouncement(`Now working in ${view ? triggerLabel({ view, loading: false }).spoken : allRow.label}`);
    setOpen(false);
  };
  const onOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) setAnnouncement('');
    else setQuery('');
  };
  const openOnArrowDown = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
    }
  };

  const rowFor = (view: ProjectView, qualify: boolean) => (
    <ProjectRow
      key={`${view.project.organizationId}:${view.project.id}`}
      view={view}
      current={view.project.id === currentId}
      qualify={qualify}
      disabled={!pickPayload(view, homeKnown, workspacesSettled)}
      onPick={() => pick(view)}
    />
  );

  return (
    <>
      <Popover open={open} onOpenChange={onOpenChange}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={variant === 'strip' ? styles.stripTrigger : `${topbar.switcher} ${styles.trigger}`}
            title={title}
            aria-label={label.spoken}
            aria-busy={label.kind === 'loading' || undefined}
            disabled={label.kind === 'loading'}
            onKeyDown={openOnArrowDown}
          >
            <FolderIcon size={14} color="var(--vn-fg-muted)" />
            <TriggerText label={label} />
            <ChevronsUpDown size={12} strokeWidth={2.5} color="var(--vn-fg-subtle)" className={styles.fixed} />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className={styles.panel}
        >
          <Command shouldFilter={false} loop label="Working project">
            <div className={model.showSearch ? undefined : styles.srOnly}>
              <Command.Input ref={searchRef} value={query} onValueChange={setQuery} placeholder="Search projects" />
            </div>
            <Command.List label="Projects" className={styles.list}>
              {!searching && back && (
                <Command.Item
                  value="back-to-home"
                  className={styles.item}
                  onSelect={() => {
                    back();
                    setAnnouncement('Now working in your organization');
                    setOpen(false);
                  }}
                >
                  <span className={styles.itemName}>{BACK_ROW_LABEL}</span>
                </Command.Item>
              )}
              {!searching && (
                <Command.Item value="all-projects" className={styles.item} onSelect={() => pick()}>
                  <span className={styles.itemMain}>
                    <span className={styles.itemName}>{allRow.label}</span>
                    {allRow.sub && <span className={styles.itemSub}>{allRow.sub}</span>}
                  </span>
                  <span className={styles.itemEnd}>{!currentId && <Check size={14} aria-label="current" />}</span>
                </Command.Item>
              )}
              {isLoading && <div className={styles.itemPlain}>Loading…</div>}
              {!isLoading && views.length === 0 && !sharedLoading && <div className={styles.itemPlain}>No projects yet</div>}
              {searching && model.results.length === 0 && (
                <div className={styles.itemPlain} role="status">{`No projects match "${query.trim()}"`}</div>
              )}
              {searching && model.results.map((v) => rowFor(v, v.foreign || v.ambiguous))}
              {model.groups.map((g) => (
                <Command.Group
                  key={g.key}
                  heading={
                    g.tooltip ? (
                      <Tooltip content={g.tooltip} side="right">
                        <span>{g.heading}</span>
                      </Tooltip>
                    ) : (
                      g.heading
                    )
                  }
                >
                  {g.rows.map((v) => rowFor(v, false))}
                </Command.Group>
              ))}
              {!searching && sharedLoading && (
                <div role="group" aria-label={SHARED_HEADING} aria-busy="true" className={styles.sharedGroup}>
                  <div className={styles.sharedHeading}>{SHARED_HEADING}</div>
                  <Skeleton className={styles.skeletonRow} height="var(--vn-control-height-sm)" />
                  <Skeleton className={styles.skeletonRow} height="var(--vn-control-height-sm)" />
                </div>
              )}
              {!searching && sharedFailed && (
                <div role="group" aria-label={SHARED_HEADING} className={styles.sharedGroup}>
                  <div className={styles.sharedHeading}>{SHARED_HEADING}</div>
                  <div className={styles.itemPlain} role="status" aria-disabled="true">
                    {failedRow().text}
                  </div>
                  <Command.Item value="shared-retry" className={`${styles.item} ${styles.retry}`} onSelect={() => void refetch()}>
                    <RotateCw size={14} aria-hidden />
                    {failedRow().retry}
                  </Command.Item>
                </div>
              )}
              {!searching && model.more > 0 && (
                <Command.Item value="search-all" className={styles.item} onSelect={() => searchRef.current?.focus()}>
                  {searchAllText(views.length)}
                </Command.Item>
              )}
              <Command.Separator className={styles.separator} />
              {showOpenProject({ workingId, shownForeign: !!shown?.foreign, workspaceOrgName: workspaceOrg }) && (
                <Command.Item
                  value="open-project"
                  className={styles.item}
                  onSelect={() => {
                    setOpen(false);
                    navigate(`${paths.organizations.base}/projects/${workingId}`);
                  }}
                >
                  Open this project
                </Command.Item>
              )}
              <Command.Item
                value="all-list"
                className={styles.item}
                onSelect={() => {
                  setOpen(false);
                  navigate('/projects');
                }}
              >
                View all projects
              </Command.Item>
            </Command.List>
          </Command>
        </PopoverContent>
      </Popover>
      <span className={styles.srOnly} role="status" aria-live="polite">
        {announcement}
      </span>
    </>
  );
}
